import React, { Component } from "react";
import { Field, Form, FormikProps, withFormik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import Button from "@material-ui/core/Button";

import ActionFormButton from "../../../common/ActionFormButton";
import VideoUpload from "../../../common/VideoUpload";
import {
  getRowItemObject,
  renderRowWithSingleItem,
  renderRowWithTwoItems,
  renderSectionTitle,
  Section,
  Wrapper,
} from "../../../common/FormUtils";
import { SubscriptionLandingInfo, SubscriptionPlan, FAQ } from "../../../../types/Paywall";
import { Community } from "../../../../types/Community";
import { generatePaywallFromCommunity, mergeGeneratedPlans } from "./PaywallGenerator.api";

type FormValues = {
  title: string;
  subtitle: string;
  buttonText: string;
  featuresTitle: string;
  testimonialsTitle: string;
  videoURL: string;
  motivationalPrompt: string;
  importJsonText: string;
  _features?: string[];
  _products?: string[];
  _plans?: SubscriptionPlan[];
  _faqs?: FAQ[];
};

type OuterProps = {
  onChageFormToEditMode: () => void;
  onRemoveItem: () => void;
  onCreateItem: (paywall: SubscriptionLandingInfo & { id?: string }) => void | Promise<void>;
  onEditItem: (paywall: SubscriptionLandingInfo & { id?: string }) => void | Promise<void>;
  mode: string;
  item: SubscriptionLandingInfo & { id?: string };
  communityId?: string;
  community?: Partial<Community>;
  isActive?: boolean;
};

type Props = FormikProps<FormValues> & OuterProps;

type State = {
  features: string[];
  products: string[];
  plans: SubscriptionPlan[];
  faqs: FAQ[];
  editingFeature: { index: number; value: string } | null;
  editingProduct: { index: number; value: string } | null;
  editingPlan: { index: number; plan: SubscriptionPlan } | null;
  editingFaq: { index: number; faq: FAQ } | null;
  importError: string | null;
  isGenerating: boolean;
  generateError: string | null;
};

const VideoFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: var(--space-3) 0;
`;

const VideoFieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const VideoFieldHint = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const DetailVideo = styled.video`
  width: 100%;
  max-height: 200px;
  border-radius: var(--radius-sm);
  display: block;
`;

class PaywallForm extends Component<Props, State> {
  state: State = {
    features: this.props.item?.features || [],
    products: this.props.item?.products || [],
    plans: this.props.item?.plans || [],
    faqs: this.props.item?.faqs || [],
    editingFeature: null,
    editingProduct: null,
    editingPlan: null,
    editingFaq: null,
    importError: null,
    isGenerating: false,
    generateError: null,
  };

  getStateData = () => {
    return {
      features: this.state.features,
      products: this.state.products,
      plans: this.state.plans,
      faqs: this.state.faqs,
    };
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.item !== this.props.item) {
      this.setState({
        features: this.props.item?.features || [],
        products: this.props.item?.products || [],
        plans: this.props.item?.plans || [],
        faqs: this.props.item?.faqs || [],
      });
    }
  }

  // Features Management
  addFeature = () => {
    this.setState({ editingFeature: { index: -1, value: "" } });
  };

  saveFeature = () => {
    const { editingFeature, features } = this.state;
    if (!editingFeature) return;

    const newFeatures = [...features];
    if (editingFeature.index === -1) {
      newFeatures.push(editingFeature.value);
    } else {
      newFeatures[editingFeature.index] = editingFeature.value;
    }

    this.setState({ features: newFeatures, editingFeature: null });
  };

  removeFeature = (index: number) => {
    const newFeatures = this.state.features.filter((_, i) => i !== index);
    this.setState({ features: newFeatures });
  };

  // Products Management
  addProduct = () => {
    this.setState({ editingProduct: { index: -1, value: "" } });
  };

  saveProduct = () => {
    const { editingProduct, products } = this.state;
    if (!editingProduct) return;

    const newProducts = [...products];
    if (editingProduct.index === -1) {
      newProducts.push(editingProduct.value);
    } else {
      newProducts[editingProduct.index] = editingProduct.value;
    }

    this.setState({ products: newProducts, editingProduct: null });
  };

  removeProduct = (index: number) => {
    const newProducts = this.state.products.filter((_, i) => i !== index);
    this.setState({ products: newProducts });
  };

  // Plans Management
  addPlan = () => {
    this.setState({
      editingPlan: {
        index: -1,
        plan: {
          id: "",
          title: "",
          price: "",
          subtitle: "",
          description: "",
          products: [],
          type: "",
        },
      },
    });
  };

  savePlan = () => {
    const { editingPlan, plans } = this.state;
    if (!editingPlan) return;

    const newPlans = [...plans];
    if (editingPlan.index === -1) {
      newPlans.push(editingPlan.plan);
    } else {
      newPlans[editingPlan.index] = editingPlan.plan;
    }

    this.setState({ plans: newPlans, editingPlan: null });
  };

  removePlan = (index: number) => {
    const newPlans = this.state.plans.filter((_, i) => i !== index);
    this.setState({ plans: newPlans });
  };

  updateEditingPlan = (field: keyof SubscriptionPlan, value: any) => {
    if (!this.state.editingPlan) return;

    this.setState({
      editingPlan: {
        ...this.state.editingPlan,
        plan: {
          ...this.state.editingPlan.plan,
          [field]: value,
        },
      },
    });
  };

  // FAQs Management
  addFaq = () => {
    this.setState({
      editingFaq: {
        index: -1,
        faq: { question: "", answer: "" },
      },
    });
  };

  saveFaq = () => {
    const { editingFaq, faqs } = this.state;
    if (!editingFaq) return;

    const newFaqs = [...faqs];
    if (editingFaq.index === -1) {
      newFaqs.push(editingFaq.faq);
    } else {
      newFaqs[editingFaq.index] = editingFaq.faq;
    }

    this.setState({ faqs: newFaqs, editingFaq: null });
  };

  removeFaq = (index: number) => {
    const newFaqs = this.state.faqs.filter((_, i) => i !== index);
    this.setState({ faqs: newFaqs });
  };

  updateEditingFaq = (field: keyof FAQ, value: string) => {
    if (!this.state.editingFaq) return;

    this.setState({
      editingFaq: {
        ...this.state.editingFaq,
        faq: {
          ...this.state.editingFaq.faq,
          [field]: value,
        },
      },
    });
  };

  renderBasicInfoSection = () => {
    const { mode, communityId, item, values } = this.props;
    const titleInput = getRowItemObject("Title", "Landing page title", "text", "title");
    const subtitleInput = getRowItemObject("Subtitle", "Landing page subtitle", "text", "subtitle");
    const buttonTextInput = getRowItemObject("Button text", "CTA button text", "text", "buttonText");
    const featuresTitleInput = getRowItemObject("Features title", "Section title for features", "text", "featuresTitle");
    const testimonialsTitle = getRowItemObject("Testimonials title", "Section title for testimonials", "text", "testimonialsTitle");
    const motivationalPrompt = getRowItemObject("Motivational prompt", "Optional motivational prompt", "text", "motivationalPrompt");

    // Group uploaded paywall videos per community so they're easy to find and
    // don't collide between brands.
    const videoStorageId = communityId || item?.id || "paywall";

    return (
      <Section>
        {renderSectionTitle("Basic Information")}
        {renderRowWithSingleItem(titleInput, this.props)}
        {renderRowWithSingleItem(subtitleInput, this.props)}
        {renderRowWithTwoItems(featuresTitleInput, testimonialsTitle, this.props)}
        {renderRowWithSingleItem(buttonTextInput, this.props)}

        <VideoFieldWrap>
          <VideoFieldLabel>Paywall video</VideoFieldLabel>
          <VideoFieldHint>
            Upload the coach intro video. It autoplays muted, loops, and fills the
            hero on the paywall. Use a short MP4 (a vertical or square clip under
            ~30s works best).
          </VideoFieldHint>
          {mode === "detail" ? (
            values.videoURL ? (
              <DetailVideo src={values.videoURL} controls />
            ) : (
              <VideoFieldHint>No video uploaded.</VideoFieldHint>
            )
          ) : (
            <Field name="videoURL">
              {({ field, form }: any) => (
                <VideoUpload
                  field={field}
                  form={{ setFieldValue: form.setFieldValue }}
                  subactionId={videoStorageId}
                  reference="paywallVideos"
                />
              )}
            </Field>
          )}
        </VideoFieldWrap>

        {renderRowWithSingleItem(motivationalPrompt, this.props)}
      </Section>
    );
  };

  renderFeaturesSection = () => {
    const { mode } = this.props;
    const { features, editingFeature } = this.state;

    return (
      <Section>
        {renderSectionTitle("Features")}
        <div style={{ marginBottom: 16 }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                marginBottom: 8,
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 4,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ flex: 1 }}>{feature}</span>
              {mode !== "detail" && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => this.setState({ editingFeature: { index, value: feature } })}
                  >
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="secondary" onClick={() => this.removeFeature(index)}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}

          {editingFeature && (
            <div style={{ marginTop: 16, padding: 16, border: "1px solid #ddd", borderRadius: 4, backgroundColor: "#f9f9f9" }}>
              <textarea
                value={editingFeature.value}
                onChange={(e) => this.setState({ editingFeature: { ...editingFeature, value: e.target.value } })}
                style={{ width: "100%", padding: 8, minHeight: 60 }}
                placeholder="Enter feature description"
              />
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <Button variant="contained" color="primary" onClick={this.saveFeature}>
                  Save
                </Button>
                <Button variant="outlined" onClick={() => this.setState({ editingFeature: null })}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {mode !== "detail" && !editingFeature && (
            <Button variant="contained" color="primary" onClick={this.addFeature} style={{ marginTop: 8 }}>
              Add Feature
            </Button>
          )}
        </div>
      </Section>
    );
  };

  renderProductsSection = () => {
    const { mode } = this.props;
    const { products, editingProduct } = this.state;

    return (
      <Section>
        {renderSectionTitle("Products")}
        <div style={{ marginBottom: 16 }}>
          {products.map((product, index) => (
            <div
              key={index}
              style={{
                marginBottom: 8,
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 4,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ flex: 1 }}>{product}</span>
              {mode !== "detail" && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => this.setState({ editingProduct: { index, value: product } })}
                  >
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="secondary" onClick={() => this.removeProduct(index)}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}

          {editingProduct && (
            <div style={{ marginTop: 16, padding: 16, border: "1px solid #ddd", borderRadius: 4, backgroundColor: "#f9f9f9" }}>
              <input
                type="text"
                value={editingProduct.value}
                onChange={(e) => this.setState({ editingProduct: { ...editingProduct, value: e.target.value } })}
                style={{ width: "100%", padding: 8 }}
                placeholder="Enter product name"
              />
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <Button variant="contained" color="primary" onClick={this.saveProduct}>
                  Save
                </Button>
                <Button variant="outlined" onClick={() => this.setState({ editingProduct: null })}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {mode !== "detail" && !editingProduct && (
            <Button variant="contained" color="primary" onClick={this.addProduct} style={{ marginTop: 8 }}>
              Add Product
            </Button>
          )}
        </div>
      </Section>
    );
  };

  renderPlansSection = () => {
    const { mode } = this.props;
    const { plans, editingPlan } = this.state;

    return (
      <Section>
        {renderSectionTitle("Subscription Plans")}
        <div style={{ marginBottom: 16 }}>
          {plans.map((plan, index) => (
            <div
              key={index}
              style={{
                marginBottom: 16,
                padding: 16,
                border: "1px solid #ddd",
                borderRadius: 4,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 style={{ margin: 0 }}>{plan.title || "Unnamed Plan"}</h4>
                {mode !== "detail" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => this.setState({ editingPlan: { index, plan: { ...plan } } })}
                    >
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => this.removePlan(index)}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
              <p style={{ margin: "8px 0 0 0", fontSize: 12, color: "#666" }}>
                {plan.price} | {plan.subtitle}
              </p>
            </div>
          ))}

          {editingPlan && (
            <div style={{ marginTop: 16, padding: 16, border: "1px solid #ddd", borderRadius: 4, backgroundColor: "#f9f9f9" }}>
              <h4>Edit Plan</h4>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Plan ID</label>
                <input
                  type="text"
                  value={editingPlan.plan.id}
                  onChange={(e) => this.updateEditingPlan("id", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                  placeholder="e.g., monthly, yearly"
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Title</label>
                <input
                  type="text"
                  value={editingPlan.plan.title}
                  onChange={(e) => this.updateEditingPlan("title", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Price</label>
                <input
                  type="text"
                  value={editingPlan.plan.price}
                  onChange={(e) => this.updateEditingPlan("price", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                  placeholder="e.g., €99.99 / month"
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Subtitle</label>
                <input
                  type="text"
                  value={editingPlan.plan.subtitle}
                  onChange={(e) => this.updateEditingPlan("subtitle", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Description</label>
                <textarea
                  value={editingPlan.plan.description}
                  onChange={(e) => this.updateEditingPlan("description", e.target.value)}
                  style={{ width: "100%", padding: 8, minHeight: 60 }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Type (optional)</label>
                <input
                  type="text"
                  value={editingPlan.plan.type || ""}
                  onChange={(e) => this.updateEditingPlan("type", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                  placeholder="e.g., Popular, Best Value"
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>
                  Products (comma-separated)
                </label>
                <textarea
                  value={editingPlan.plan.products.join(", ")}
                  onChange={(e) =>
                    this.updateEditingPlan(
                      "products",
                      e.target.value.split(",").map((p) => p.trim())
                    )
                  }
                  style={{ width: "100%", padding: 8, minHeight: 60 }}
                />
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <Button variant="contained" color="primary" onClick={this.savePlan}>
                  Save Plan
                </Button>
                <Button variant="outlined" onClick={() => this.setState({ editingPlan: null })}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {mode !== "detail" && !editingPlan && (
            <Button variant="contained" color="primary" onClick={this.addPlan} style={{ marginTop: 8 }}>
              Add Plan
            </Button>
          )}
        </div>
      </Section>
    );
  };

  renderFaqsSection = () => {
    const { mode } = this.props;
    const { faqs, editingFaq } = this.state;

    return (
      <Section>
        {renderSectionTitle("FAQs")}
        <div style={{ marginBottom: 16 }}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                marginBottom: 16,
                padding: 16,
                border: "1px solid #ddd",
                borderRadius: 4,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <strong>{faq.question}</strong>
                  <p style={{ margin: "8px 0 0 0", color: "#666" }}>{faq.answer}</p>
                </div>
                {mode !== "detail" && (
                  <div style={{ display: "flex", gap: 8, marginLeft: 16 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => this.setState({ editingFaq: { index, faq: { ...faq } } })}
                    >
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => this.removeFaq(index)}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {editingFaq && (
            <div style={{ marginTop: 16, padding: 16, border: "1px solid #ddd", borderRadius: 4, backgroundColor: "#f9f9f9" }}>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Question</label>
                <input
                  type="text"
                  value={editingFaq.faq.question}
                  onChange={(e) => this.updateEditingFaq("question", e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 4 }}>Answer</label>
                <textarea
                  value={editingFaq.faq.answer}
                  onChange={(e) => this.updateEditingFaq("answer", e.target.value)}
                  style={{ width: "100%", padding: 8, minHeight: 60 }}
                />
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <Button variant="contained" color="primary" onClick={this.saveFaq}>
                  Save FAQ
                </Button>
                <Button variant="outlined" onClick={() => this.setState({ editingFaq: null })}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {mode !== "detail" && !editingFaq && (
            <Button variant="contained" color="primary" onClick={this.addFaq} style={{ marginTop: 8 }}>
              Add FAQ
            </Button>
          )}
        </div>
      </Section>
    );
  };

  handleApplyJsonText = () => {
    const { setValues, values } = this.props;
    if (!values?.importJsonText) return;

    try {
      const data = JSON.parse(values.importJsonText) as SubscriptionLandingInfo;
      setValues({
        ...values,
        title: data.title || values.title,
        subtitle: data.subtitle || values.subtitle,
        buttonText: data.buttonText || values.buttonText,
        featuresTitle: data.featuresTitle || values.featuresTitle,
        testimonialsTitle: data.testimonialsTitle || values.testimonialsTitle,
        videoURL: data.videoURL || values.videoURL,
        motivationalPrompt: data.motivationalPrompt || values.motivationalPrompt,
        importJsonText: values.importJsonText,
      });

      this.setState({
        features: data.features || [],
        products: data.products || [],
        plans: data.plans || [],
        faqs: data.faqs || [],
        importError: null,
      });
    } catch (error) {
      console.error("Invalid JSON provided", error);
      this.setState({
        importError: "Invalid JSON. Please check the format. " + (error as Error)?.message,
      });
    }
  };

  handleGenerateFromCommunity = async () => {
    const { community, communityId, setValues, values } = this.props;

    this.setState({ isGenerating: true, generateError: null });

    try {
      const draft = await generatePaywallFromCommunity({
        community,
        communityId,
      });

      setValues({
        ...values,
        title: draft.title || values.title,
        subtitle: draft.subtitle || values.subtitle,
        buttonText: draft.buttonText || values.buttonText,
        featuresTitle: draft.featuresTitle || values.featuresTitle,
        testimonialsTitle: draft.testimonialsTitle || values.testimonialsTitle,
        motivationalPrompt: draft.motivationalPrompt || values.motivationalPrompt,
      });

      this.setState((prev) => ({
        features: draft.features.length ? draft.features : prev.features,
        faqs: draft.faqs.length ? draft.faqs : prev.faqs,
        // Merge plan copy onto existing plans so RevenueCat ids/prices survive.
        plans: mergeGeneratedPlans(prev.plans, draft.plans),
        isGenerating: false,
        generateError: null,
      }));
    } catch (error) {
      console.error("Paywall generation failed", error);
      this.setState({
        isGenerating: false,
        generateError:
          "Could not generate the paywall. " + ((error as Error)?.message || ""),
      });
    }
  };

  renderGenerateSection = () => {
    const { community, communityId } = this.props;
    const { isGenerating, generateError } = this.state;
    const canGenerate = Boolean(communityId || community?.title);

    return (
      <Section>
        {renderSectionTitle("Generate from community")}
        <p style={{ margin: "0 0 12px 0", color: "#666", fontSize: 13 }}>
          Draft the paywall copy from this community's information. Review and edit before
          saving. Plan prices and product ids are kept as is.
        </p>
        <Button
          variant="contained"
          color="primary"
          disabled={!canGenerate || isGenerating}
          onClick={(event) => {
            event.preventDefault();
            this.handleGenerateFromCommunity();
          }}
        >
          {isGenerating ? "Generating..." : "Generate paywall"}
        </Button>
        {!canGenerate && (
          <p style={{ color: "#999", marginTop: 8, fontSize: 12 }}>
            Save the community information first to enable generation.
          </p>
        )}
        {generateError && <p style={{ color: "red", marginTop: 8 }}>{generateError}</p>}
      </Section>
    );
  };

  renderImportSection = () => {
    const importJsonField = getRowItemObject(
      "Import JSON",
      "Paste paywall JSON here",
      "textarea",
      "importJsonText"
    );

    return (
      <Section>
        {renderSectionTitle("Import JSON")}
        {renderRowWithSingleItem(importJsonField, this.props)}
        <Button
          variant="outlined"
          color="primary"
          onClick={(event) => {
            event.preventDefault();
            this.handleApplyJsonText();
          }}
        >
          Apply JSON
        </Button>
        {this.state.importError && (
          <p style={{ color: "red", marginTop: 8 }}>{this.state.importError}</p>
        )}
      </Section>
    );
  };

  handleFormSubmit = () => {
    const { setFieldValue, handleSubmit } = this.props;
    // Store state data in Formik values before submitting
    setFieldValue("_features", this.state.features);
    setFieldValue("_products", this.state.products);
    setFieldValue("_plans", this.state.plans);
    setFieldValue("_faqs", this.state.faqs);
    handleSubmit();
  };

  render() {
    const { onChageFormToEditMode, onRemoveItem, isSubmitting, mode, isActive } = this.props;

    return (
      <Wrapper>
        <Form>
          {mode !== "detail" && this.renderGenerateSection()}
          {mode !== "detail" && this.renderImportSection()}
          {this.renderBasicInfoSection()}
          {this.renderFeaturesSection()}
          {this.renderProductsSection()}
          {this.renderPlansSection()}
          {this.renderFaqsSection()}
          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={() => {
              console.log("Handling Submit");
              this.handleFormSubmit();
            }}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Paywall"
            canBeRemoved
            mode={mode}
            hidden={!isActive}
          />
        </Form>
      </Wrapper>
    );
  }
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  subtitle: Yup.string().required("Subtitle is required"),
  buttonText: Yup.string().required("Button text is required"),
  featuresTitle: Yup.string().required("Features title is required"),
  testimonialsTitle: Yup.string().required("Testimonials title is required"),
});

const enhance = withFormik<OuterProps, FormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ item }: OuterProps): FormValues => ({
    title: item?.title || "",
    subtitle: item?.subtitle || "",
    buttonText: item?.buttonText || "",
    featuresTitle: item?.featuresTitle || "",
    testimonialsTitle: item?.testimonialsTitle || "",
    videoURL: item?.videoURL || "",
    motivationalPrompt: item?.motivationalPrompt || "",
    importJsonText: "",
    _features: item?.features || [],
    _products: item?.products || [],
    _plans: item?.plans || [],
    _faqs: item?.faqs || [],
  }),
  validationSchema,
  handleSubmit(values, { setSubmitting, props }) {
    console.log("Submitting Paywall form with values:", values);

    const { onCreateItem, onEditItem, mode, item } = props;
    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    const paywall: any = {
      id: item?.id,
      title: values.title,
      subtitle: values.subtitle,
      buttonText: values.buttonText,
      featuresTitle: values.featuresTitle,
      testimonialsTitle: values.testimonialsTitle,
      features: values._features || [],
      products: values._products || [],
      plans: values._plans || [],
      faqs: values._faqs || [],
      testimonials: item?.testimonials || [],
    };

    // Only add optional fields if they have values
    if (values.videoURL && values.videoURL.trim()) {
      paywall.videoURL = values.videoURL;
    }
    if (values.motivationalPrompt && values.motivationalPrompt.trim()) {
      paywall.motivationalPrompt = values.motivationalPrompt;
    }

    properCallback(paywall);
    setSubmitting(false);
  },
  //@ts-ignore
})(PaywallForm);

export default enhance;
